import {
  IExpenseItem,
  IExpenseParticipant,
  IExpenseRateio,
} from '../pages/Dashboard/vehicles_management/Expenses';

const DRAFT_VERSION = 1;
const DRAFT_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 dias
const DRAFT_KEY_PREFIX = '@Samasc:expenseReportDraft:';

// Item persistido no rascunho — igual a IExpenseItem, mas sem os campos File
// (comprovante e fotos de KM não podem ir para o localStorage). Quando o item
// original tinha um arquivo, marcamos a pendência para o usuário reanexar ao
// retomar o rascunho, em vez de restaurar o item como se tivesse comprovante.
export interface IDraftExpenseItem
  extends Omit<
    IExpenseItem,
    'receipt' | 'receipt_km_start' | 'receipt_km_end'
  > {
  receiptPending?: boolean;
  receiptFileName?: string;
  receiptKmStartPending?: boolean;
  receiptKmEndPending?: boolean;
}

export interface IExpenseReportDraftData {
  step: number;
  reportDate: string;
  managerId: string;
  participants: IExpenseParticipant[];
  rateio: IExpenseRateio;
  items: IDraftExpenseItem[];
  costCenterPerItem: boolean;
  itemCostCenters: Record<string, string>;
  ajudaCustoDueDate: string;
  useDirectorKmRate: boolean;
  kmStart: string;
  kmEnd: string;
}

interface IDraftEnvelope {
  version: number;
  savedAt: number;
  data: IExpenseReportDraftData;
}

const getDraftKey = (userId: string) => `${DRAFT_KEY_PREFIX}${userId}`;

// Remove os arquivos dos itens antes de serializar — File não sobrevive a um
// JSON.stringify/parse. O item volta na lista, só sem o arquivo.
const stripFilesFromItems = (items: IExpenseItem[]): IDraftExpenseItem[] =>
  items.map(item => {
    const { receipt, receipt_km_start, receipt_km_end, ...rest } = item;
    const draftItem: IDraftExpenseItem = { ...rest };

    if (receipt instanceof File) {
      draftItem.receiptPending = true;
      draftItem.receiptFileName = receipt.name;
    }
    if (receipt_km_start instanceof File) {
      draftItem.receiptKmStartPending = true;
    }
    if (receipt_km_end instanceof File) {
      draftItem.receiptKmEndPending = true;
    }

    return draftItem;
  });

export function saveExpenseReportDraft(
  userId: string,
  data: IExpenseReportDraftData,
): void {
  if (!userId) return;
  try {
    const envelope: IDraftEnvelope = {
      version: DRAFT_VERSION,
      savedAt: Date.now(),
      data: {
        ...data,
        items: stripFilesFromItems(data.items as IExpenseItem[]),
      },
    };
    localStorage.setItem(getDraftKey(userId), JSON.stringify(envelope));
  } catch (e) {
    // localStorage indisponível/cheio — rascunho é best-effort e não pode
    // impedir o preenchimento do relatório.
  }
}

export function loadExpenseReportDraft(
  userId: string,
): IExpenseReportDraftData | null {
  if (!userId) return null;
  try {
    const raw = localStorage.getItem(getDraftKey(userId));
    if (!raw) return null;

    const envelope: IDraftEnvelope = JSON.parse(raw);
    if (envelope?.version !== DRAFT_VERSION) return null;
    if (!envelope.savedAt || Date.now() - envelope.savedAt > DRAFT_TTL_MS) {
      return null;
    }
    if (!envelope.data || !Array.isArray(envelope.data.items)) return null;

    return envelope.data;
  } catch (e) {
    return null;
  }
}

export function clearExpenseReportDraft(userId: string): void {
  if (!userId) return;
  try {
    localStorage.removeItem(getDraftKey(userId));
  } catch (e) {
    // ignora — não há rascunho para limpar mesmo
  }
}

// Um rascunho "vazio" (relatório recém-aberto, nada preenchido) não vale a
// pena persistir nem perguntar ao usuário se quer continuar.
export function isDraftEmpty(data: IExpenseReportDraftData): boolean {
  return (
    !data.reportDate &&
    !data.managerId &&
    data.items.length === 0 &&
    data.participants.every(p => !p.user_id)
  );
}
